import styled from 'styled-components';
import { Dropdown } from 'semantic-ui-react';
import colors from '../../../../../common/styles/colors';
import {
  WarningBox,
  WarningIcon,
  CouplingDescription,
  SensorSelectionCard,
  SensorCardLeft
} from '../EventDetection.styled';

// Main Container
export const CouplingContainer = styled.div`
  padding-left: 4px;
  padding-right: 4px;
  margin-bottom: ${props => (props.embedded ? '0' : '24px')};
`;

export const CouplingAlternatesSection = styled.div`
  padding: 16px 20px 20px;
  margin-bottom: 16px;
  background-color: ${colors.greyXL};
  border: 1px solid ${colors.greyL};
  border-radius: 12px;
`;

export const CouplingAlternatesHeader = styled.div`
  margin-bottom: 16px;
`;

export const CouplingAlternatesTitle = styled.div`
  font-size: 0.8em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${colors.greyD};
  margin-bottom: 4px;
`;

export const CouplingAlternatesHint = styled.div`
  font-size: 0.9em;
  color: ${colors.greyXD};
  line-height: 1.4;
`;

export const CouplingAlternateRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid ${props => (props.selected ? props.theme.primaryColor : colors.greyL)};
  background-color: ${props => (props.selected ? colors.white : 'transparent')};
  opacity: ${props => (props.dimmed ? 0.55 : 1)};
  cursor: ${props => (props.dimmed ? 'not-allowed' : 'default')};
  transition: border-color 0.2s ease, background-color 0.2s ease, opacity 0.2s ease;
`;

export const CouplingAlternateRadio = styled.div`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid ${props => (props.selected ? props.theme.primaryColor : colors.grey)};
  background-color: ${props => (props.selected ? props.theme.primaryColor : 'transparent')};
  box-shadow: ${props => (props.selected ? `inset 0 0 0 3px ${colors.white}` : 'none')};
`;

export const CouplingAlternateRowContent = styled.div`
  flex: 1;
  min-width: 0;
  pointer-events: ${props => (props.blockInteraction ? 'none' : 'auto')};
`;

export const CouplingAlternatesDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 10px 0;
  color: ${colors.greyXD};
  font-size: 0.85em;
  font-weight: 600;
  text-transform: uppercase;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: ${colors.greyL};
  }
`;

export const ModernCouplingSection = styled.div`
  padding: 20px;
  margin-bottom: ${props => (props.embedded ? '0' : '32px')};
  background-color: ${colors.greyXL};
  border: 1px solid ${colors.greyL};
  border-radius: 12px;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const CouplingHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.isExpanded ? '20px' : '0'};
`;

export const CollapseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  color: ${colors.greyD};
  font-size: 0.85em;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${colors.black};
  }
  
  svg {
    margin-left: 4px;
    transition: transform 0.2s ease;
    transform: ${props => props.isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
  }
`;

export const CollapsibleContent = styled.div`
  overflow: hidden;
  max-height: ${props => props.isExpanded ? '3000px' : '0'};
  opacity: ${props => props.isExpanded ? '1' : '0'};
  transition: max-height 0.3s ease, opacity 0.3s ease;
`;

// Toggle Switch Components
export const ModernToggleSwitch = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
`;

export const ToggleSwitchTrack = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background-color: ${props => props.checked ? props.theme.primaryColor : colors.grey};
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${(props) => {
    if (props.disabled) return props.checked ? props.theme.primaryColor : colors.grey;
    return props.checked ? props.theme.primaryColor : colors.greyD;
  }};
    filter: ${(props) => {
    if (props.disabled) return 'none';
    return props.checked ? 'brightness(0.9)' : 'none';
  }};
  }
`;

export const ToggleSwitchThumb = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: ${colors.white};
  transform: ${props => props.checked ? 'translateX(19px)' : 'translateX(3px)'};
  transition: transform 0.2s ease;
`;

export const ToggleSwitchLabel = styled.span`
  font-size: 0.95em;
  font-weight: 600;
  color: ${props => props.checked ? colors.black : colors.greyXD};
  transition: color 0.2s ease;
`;

export const ToggleSwitchWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  user-select: none;
  opacity: ${props => props.disabled ? 0.6 : 1};
`;

export const LoadingIndicator = styled.span`
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
  
  .ui.loader {
    margin-right: 6px;
  }
`;

export const LoadingText = styled.span`
  font-size: 12px;
  color: #666;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

export const LinkedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background-color: ${props => `${props.theme.primaryColor}20`};
  border: 1px solid ${props => `${props.theme.primaryColor}40`};
  border-radius: 4px;
  font-size: 0.75em;
  font-weight: 700;
  color: ${props => props.theme.primaryColor};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

// Warning Components
export const WarningBoxWithMargin = styled(WarningBox)`
  margin-bottom: 20px;
`;

export { WarningIcon };

export const WarningList = styled.ul`
  margin-top: 8px;
  margin-bottom: 0;
`;

export const WarningParagraph = styled.p`
  margin-top: 8px;
  margin-bottom: 0;
`;

// Info Components (similar to warning but with blue/info color scheme)
export const InfoBoxWithMargin = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 20px;
  background-color: rgba(33, 150, 243, 0.08);
  border: 1px solid rgba(33, 150, 243, 0.3);
  border-radius: 8px;
  font-size: 0.9em;
  line-height: 1.5;
  color: ${colors.black};
`;

export const InfoIcon = styled.div`
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
  flex-shrink: 0;
  
  svg {
    width: 18px;
    height: 18px;
    color: #2196F3;
  }
`;

export const InfoList = styled.ul`
  margin-top: 8px;
  margin-bottom: 8px;
  padding-left: 20px;
  
  li {
    margin-bottom: 4px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const InfoParagraph = styled.p`
  margin-top: 8px;
  margin-bottom: 0;
  font-size: 0.95em;
  color: ${colors.greyXD};
`;

// Sensor Selection Components
export const SensorSectionContainer = styled.div`
  margin-bottom: 24px;
`;

export const SensorSelectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 0;
`;

export const SensorCardWrapper = styled(SensorSelectionCard)`
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
`;

// Re-export SensorCardLeft for use in components
export { SensorCardLeft };

export const SensorCardLeftWrapper = styled(SensorCardLeft)`
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
`;

export const SensorCheckIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid ${props => props.checked ? props.theme.primaryColor : colors.grey};
  background-color: ${props => props.checked ? props.theme.primaryColor : colors.white};
  transition: all 0.2s ease;
  
  svg {
    width: 14px;
    height: 14px;
    color: ${colors.white};
    opacity: ${props => props.checked ? 1 : 0};
    transition: opacity 0.2s ease;
  }
  
  ${SensorSelectionCard}:hover & {
    border-color: ${props => props.checked ? props.theme.primaryColor : colors.greyD};
  }
`;

export const SensorIconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
  background-color: ${(props) => {
    if (!props.selected) return colors.greyXL;
    if (props.sensorType === 'temperature') return 'rgba(255, 92, 92, 0.1)';
    if (props.sensorType === 'vibration') return `${props.theme.primaryColor}20`;
    return colors.greyXL;
  }};
  transition: background-color 0.2s ease;
  
  svg {
    width: 16px;
    height: 16px;
    color: ${(props) => {
    if (!props.selected) return colors.greyD;
    if (props.sensorType === 'temperature') return colors.redL;
    if (props.sensorType === 'vibration') return props.theme.primaryColor;
    return colors.greyD;
  }};
  }
`;

export const SensorCardLabel = styled.span`
  font-size: 0.9em;
  font-weight: 500;
  color: ${props => props.selected ? colors.black : colors.greyXD};
  transition: color 0.2s ease;
`;

export const VibrationCardContainer = styled.div`
  position: relative;
`;

// Feature Dropdown Components
export const FeatureDropdownWrapper = styled.div`
  position: relative;
  z-index: 10;
`;

export const FeatureDropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 150px;
  padding: 6px 12px;
  font-size: 0.85em;
  font-weight: 500;
  color: ${colors.black};
  background-color: ${colors.white};
  border: 1px solid ${colors.greyL};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  &:hover {
    border-color: ${props => props.theme.primaryColor};
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primaryColor};
    box-shadow: 0 0 0 2px ${props => `${props.theme.primaryColor}20`};
  }
  
  svg {
    width: 14px;
    height: 14px;
    color: ${colors.greyD};
    flex-shrink: 0;
  }
`;

export const FeatureDropdownMenu = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  min-width: 224px;
  background-color: ${colors.white};
  border: 1px solid ${colors.greyL};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 20;
  overflow: hidden;
  animation: fadeIn 0.1s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

export const FeatureDropdownHeader = styled.div`
  padding: 8px 12px;
  font-size: 0.75em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.greyD};
  background-color: rgba(247, 247, 247, 0.3);
  border-bottom: 1px solid rgba(229, 232, 225, 0.5);
`;

export const FeatureCheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  background-color: ${props => props.disabled ? 'rgba(247, 247, 247, 0.5)' : 'transparent'};
  
  &:hover {
    background-color: ${props => props.disabled ? 'rgba(247, 247, 247, 0.5)' : colors.greyXXL};
  }
`;

export const FeatureCheckbox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 2px solid ${props => props.checked ? props.theme.primaryColor : colors.grey};
  background-color: ${props => props.checked ? props.theme.primaryColor : colors.white};
  transition: all 0.2s ease;
  
  svg {
    width: 10px;
    height: 10px;
    color: ${colors.white};
  }
  
  ${FeatureCheckboxItem}:hover & {
    border-color: ${(props) => {
    if (props.checked) return props.theme.primaryColor;
    if (props.disabled) return colors.grey;
    return props.theme.primaryColor;
  }};
  }
`;

export const FeatureCheckboxLabel = styled.span`
  flex: 1;
  font-size: 0.85em;
  font-weight: 500;
  color: ${props => props.checked ? colors.black : colors.greyXD};
`;

export const FeatureUnavailableBadge = styled.span`
  font-size: 0.7em;
  font-weight: 700;
  background-color: ${colors.greyL};
  color: ${colors.greyXD};
  padding: 2px 6px;
  border-radius: 3px;
`;

export const FeatureDropdownText = styled.span`
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DropdownOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10;
`;

// Configuration Components
export const ConfigSectionLabel = styled.label`
  display: block;
  font-size: 0.75em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.greyXD};
  margin-bottom: 12px;
`;

export const ConfigGridRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  border-top: 1px solid rgba(229, 232, 225, 0.5);
  margin-bottom: 20px;
  
  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const ConfigField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ThresholdToggleGroup = styled.div`
  display: flex;
  background-color: ${colors.white};
  border: 1px solid ${colors.greyL};
  border-radius: 6px;
  padding: 2px;
  gap: 0;
`;

export const ThresholdToggleButton = styled.button`
  flex: 1;
  padding: 6px 12px;
  font-size: 0.85em;
  font-weight: 500;
  color: ${props => props.active ? colors.black : colors.greyXD};
  background-color: ${props => props.active ? `${props.theme.primaryColor}20` : 'transparent'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.active ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'};
  
  &:hover {
    color: ${props => props.active ? colors.black : colors.black};
    background-color: ${props => props.active ? `${props.theme.primaryColor}20` : colors.greyXXL};
  }
  
  &:focus {
    outline: none;
  }
`;

export const HelpCursor = styled.span`
  cursor: help;
`;

export const SpeedBinDropdown = styled(Dropdown)`
  &&& {
    min-width: 100%;
    font-size: 0.85em;
    
    /* Style for disabled dropdown items */
    .item.disabled {
      opacity: 0.5;
      cursor: not-allowed !important;
    }
  }
`;

export const HelperText = styled.div`
  font-size: 0.85em;
  color: #999;
  font-style: italic;
  margin-top: 12px;
`;

// Thresholds Display Components
export const ThresholdsDisplayContainer = styled.div`
  margin-top: 20px;
  padding: 12px;
  background-color: ${colors.white};
  border: 1px solid ${colors.greyL};
  border-radius: 8px;
  font-size: 0.85em;
`;

export const ThresholdsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: ${colors.greyXD};
  font-weight: 500;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const ThresholdsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ThresholdGroupItem = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr auto;
  gap: 8px;
  align-items: baseline;
  padding: 8px 0;
  border-bottom: 1px solid rgba(229, 232, 225, 0.5);
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`;

export const ThresholdLabel = styled.span`
  font-weight: 600;
  color: ${colors.black};
  flex-shrink: 0;
  
  @media (max-width: 640px) {
    margin-bottom: 4px;
  }
`;

export const ThresholdValue = styled.span`
  font-family: 'Monaco', 'Courier New', monospace;
  color: ${colors.black};
  font-weight: 500;
`;

export const ThresholdBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.75em;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${props => props.level === 'critical' ? 'rgba(255, 92, 92, 0.1)' : 'rgba(243, 144, 10, 0.1)'};
  color: ${props => props.level === 'critical' ? colors.redL : colors.warning};
  white-space: nowrap;
  
  @media (max-width: 640px) {
    justify-self: start;
  }
`;

export const ThresholdDirections = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  grid-column: 2;
  
  @media (max-width: 640px) {
    grid-column: 1;
    margin-top: 4px;
  }
`;

export const DirectionItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.9em;
  
  &::before {
    content: '•';
    color: ${colors.greyD};
    margin-right: 2px;
  }
  
  &:first-child::before {
    display: none;
  }
`;

export const ThresholdValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// Description
export const CouplingDescriptionWrapper = styled(CouplingDescription)`
  margin-top: 12px;
  margin-left: 0;
`;

// Tooltip Components
export const TooltipTriggerIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-left: 8px;
  cursor: help;
  color: ${props => props.isWarning ? colors.warning : '#2196F3'};
  transition: color 0.2s ease;
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  &:hover {
    color: ${props => props.isWarning ? colors.warningD : '#1976D2'};
  }
`;

export const TooltipContent = styled.div`
  max-width: 400px;
  padding: 4px;
`;

export const TooltipSection = styled.div`
  padding-bottom: ${props => props.isLast ? '0' : '12px'};
  margin-bottom: ${props => props.isLast ? '0' : '12px'};
  border-bottom: ${props => props.isLast ? 'none' : `1px solid ${colors.greyL}`};
  
  &:last-child {
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: none;
  }
`;

export const TooltipSectionTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  color: ${colors.black};
`;

export const TooltipList = styled.ul`
  margin: 8px 0;
  padding-left: 20px;
  
  li {
    margin-bottom: 4px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const TooltipText = styled.p`
  margin: 8px 0 0 0;
  font-size: 0.95em;
  color: ${colors.greyXD};
  line-height: 1.4;
  
  &:first-child {
    margin-top: 0;
  }
`;
